import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, FileCheck, Shield, Users, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Tricolor Strip */}
      <div className="tricolor-strip" />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="outline" className="govt-btn-secondary" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="govt-btn-primary" data-testid="register-btn">
                  Register Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1735933109524-52d7f05dc7c1?crop=entropy&cs=srgb&fm=jpg&q=85')` }}
        />
        <div className="absolute inset-0 bg-[#003380]/90" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white font-serif mb-6 leading-tight">
              IIT Hyderabad Vendor Registration Portal
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              A unified platform for vendor registration, scrutiny, and approval aligned with 
              institutional procurement norms. Transparent, efficient, and secure.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-white text-[#003380] hover:bg-slate-100 font-semibold px-8" data-testid="hero-register-btn">
                  Start Registration
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8" data-testid="hero-login-btn">
                  Vendor Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold font-serif govt-blue-text mb-4">
              Registration Process
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Simple step-by-step process to complete your vendor registration
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: 'Create Account', desc: 'Register with email and basic details' },
              { step: 2, title: 'Fill Application', desc: 'Complete multi-step registration form' },
              { step: 3, title: 'Upload Documents', desc: 'Submit required statutory documents' },
              { step: 4, title: 'Track Status', desc: 'Monitor application and respond to queries' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#003380] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h4 className="font-semibold text-lg mb-2">{item.title}</h4>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold font-serif govt-blue-text mb-6">
                Why Register on This Portal?
              </h3>
              <div className="space-y-4">
                {[
                  'Paperless and transparent registration process',
                  'Real-time application status tracking',
                  'Participate in institutional procurement opportunities',
                  'Secure and in Compliance with IT Act 2000',
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-100 rounded-lg p-8">
              <h4 className="font-semibold text-lg mb-4">Supported Vendor Categories</h4>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Goods Suppliers', icon: '📦' },
                  { label: 'Service Providers', icon: '🔧' },
                  { label: 'Works Contractors', icon: '🏗️' },
                  { label: 'Consultants', icon: '📋' },
                  { label: 'MSME Vendors', icon: '🏭' },
                  { label: 'Startups', icon: '🚀' },
                ].map((cat, idx) => (
                  <div key={idx} className="bg-white rounded-md p-3 flex items-center gap-2">
                    <span>{cat.icon}</span>
                    <span className="text-sm font-medium">{cat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003380] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-between items-center">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <div className="text-sm text-blue-200">
              © {new Date().getFullYear()} Indian Institute of Technology Hyderabad. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
